import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const RegistrationModal = ({ webinar, onClose, onConfirm, isLoading }) => {
  const [agreed, setAgreed] = useState(false);

  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleConfirm = () => {
    if (!agreed) return;
    onConfirm(webinar?.id);
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[1000]"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-[1010] flex items-center justify-center p-4">
        <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Video" size={20} color="var(--color-primary)" />
              </div>
              <h2 className="text-lg font-heading font-semibold text-foreground">
                Register for Webinar
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
            >
              <Icon name="X" size={20} color="var(--color-muted-foreground)" />
            </button>
          </div>

          {/* Webinar Details */}
          <div className="p-5">
            {/* Thumbnail */}
            <div className="relative h-40 rounded-lg overflow-hidden mb-5">
              <Image
                src={webinar?.thumbnail_url || 'https://images.unsplash.com/photo-1540575467063-178a50c2df87'}
                alt={webinar?.title || 'Webinar thumbnail'}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-3 left-3">
                {webinar?.topic && (
                  <span className="px-2.5 py-1 bg-primary text-primary-foreground text-xs font-semibold rounded-full">
                    {webinar?.topic}
                  </span>
                )}
              </div>
            </div>

            {/* Title */}
            <h3 className="text-xl font-heading font-bold text-foreground mb-2">
              {webinar?.title}
            </h3>
            <p className="text-sm text-muted-foreground mb-5">
              {webinar?.description}
            </p>

            {/* Expert Info */}
            {webinar?.expert && (
              <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg mb-5">
                <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                  <Image
                    src={webinar?.expert?.photo_url || 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e'}
                    alt={webinar?.expert?.name || 'Expert'}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">
                    {webinar?.expert?.name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {webinar?.expert?.title}
                    {webinar?.expert?.organization && ` · ${webinar?.expert?.organization}`}
                  </p>
                </div>
              </div>
            )}

            {/* Session Details */}
            <div className="space-y-3 mb-5">
              <div className="flex items-center gap-3 text-sm">
                <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="Calendar" size={16} color="var(--color-primary)" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Date & Time</p>
                  <p className="font-medium text-foreground">{formatDate(webinar?.scheduled_at)}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 text-sm">
                <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="Clock" size={16} color="var(--color-primary)" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Duration</p>
                  <p className="font-medium text-foreground">{webinar?.duration_minutes || 60} minutes</p>
                </div>
              </div>

              <div className="flex items-center gap-3 text-sm">
                <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="Users" size={16} color="var(--color-primary)" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Registered</p>
                  <p className="font-medium text-foreground">
                    {webinar?.participant_count || 0} participants
                    {webinar?.max_participants && ` / ${webinar?.max_participants} max`}
                  </p>
                </div>
              </div>

              {webinar?.language && (
                <div className="flex items-center gap-3 text-sm">
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name="Globe" size={16} color="var(--color-primary)" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Language</p>
                    <p className="font-medium text-foreground">{webinar?.language}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Confirmation Checkbox */}
            <label className="flex items-start gap-3 cursor-pointer mb-5 p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e?.target?.checked)}
                className="mt-0.5 w-4 h-4 rounded border-border text-primary focus:ring-primary"
              />
              <span className="text-sm text-muted-foreground">
                I confirm my registration and understand I will receive a reminder email before the session starts.
              </span>
            </label>

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                fullWidth
                onClick={onClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                variant="default"
                fullWidth
                iconName="Check"
                iconPosition="left"
                onClick={handleConfirm}
                disabled={!agreed || isLoading}
              >
                {isLoading ? 'Registering...' : 'Confirm Registration'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RegistrationModal;
